import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Camera, Flower2 } from "lucide-react";
import { MemberRatingPill } from "@/design-system/MemberRatingPill";
import { useAuth } from "@/components/AuthProvider";

/**
 * Iter 28 · Vania Msg 231 — For You cross-event photo grid + reviews list.
 *
 * Data:
 *   GET /api/feed/posts    — public photo posts (no auth needed)
 *   GET /api/feed/reviews  — public member reviews
 *
 * When empty, we show a helpful next step (never a dead card).
 */
const API = process.env.REACT_APP_BACKEND_URL;
const client = axios.create({ baseURL: `${API}/api` });
const CARD = "var(--ww-surface)";
const LINE = "var(--ww-line)";
const META = "var(--ww-text-muted)";
const YELLOW = "#FFD60A";
// Wave 14.2 (Vania 2 Oct) · yellow is unreadable as text / icon / stroke on
// cream (1.3:1). Use the token for text/icon roles so the cream theme swaps
// to ink while night keeps the yellow.
const INK = "var(--ww-accent-ink)";

function absUrl(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return `${API}${u}`;
}

export function PostsGrid({ setViewer }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    client.get("/feed/posts?limit=48").then((r) => setRows(r.data.posts || [])).catch(() => setRows([]));
  }, []);
  if (rows === null) return <p style={{ fontFamily: "Arial", color: META, padding: "24px 0" }}>loading…</p>;
  if (!rows.length) return <EmptyLane
    icon={<Camera size={20} color={INK} />}
    title="no photos yet"
    line="mark went at an event, then be the first to share a photo."
    cta={{ href: "/app", label: "browse events" }} />;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 8 }}>
      {rows.flatMap((p) => (p.media_urls || []).map((u, i) => (
        <button key={`${p.id}-${i}`} data-testid={`feed-post-${p.id}-${i}`}
                onClick={() => setViewer && setViewer({ url: absUrl(u), title: p.caption || "" })}
                style={{ padding: 0, border: 0, background: "transparent", cursor: "zoom-in", aspectRatio: "1/1",
                         overflow: "hidden", borderRadius: 8 }}>
          <img src={absUrl(u)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </button>
      )))}
    </div>
  );
}

export function ReviewsFeed() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    client.get("/feed/reviews?limit=40").then((r) => setRows(r.data.reviews || [])).catch(() => setRows([]));
  }, []);
  if (rows === null) return <p style={{ fontFamily: "Arial", color: META, padding: "24px 0" }}>loading…</p>;
  if (!rows.length) return <EmptyLane
    icon={<Flower2 size={20} color={INK} />}
    title="no member reviews yet"
    line="attended something recently? leave a review from that event's page."
    cta={{ href: "/app", label: "find an event" }} />;
  // Wave C.11 (Vania 7 Oct) · reviews tab · no stars anywhere. The score is
  // the ScoreBadge roses count out of 100; show it as the deep iko number.
  // Each row surfaces the event (poster thumb · title · venue · date) so
  // tapping goes straight to the event, and the review text clamps to 2
  // lines with a "more" affordance via the open-event link.
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {rows.map((r) => (
        <article key={r.id} data-testid={`feed-review-${r.id}`}
                 style={{ background: CARD, border: `1px solid ${LINE}`, borderRadius: 14, padding: 16 }}>
          <header style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 8 }}>
            {r.event?.cover_image_url ? (
              <img src={r.event.cover_image_url} alt=""
                   data-testid={`feed-review-${r.id}-poster`}
                   style={{ width: 48, height: 64, objectFit: "cover", borderRadius: 6,
                            flexShrink: 0, border: `1px solid ${LINE}` }} />
            ) : (
              <div style={{ width: 48, height: 64, borderRadius: 6, background: "var(--ww-sunken)",
                             border: `1px solid ${LINE}`, flexShrink: 0 }} />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <Link to={`/app/event/${r.event_id}`} data-testid={`feed-review-${r.id}-title`}
                    style={{ display: "block", fontFamily: "Poppins", fontWeight: 800, fontSize: 15,
                             color: "var(--ww-text)", textTransform: "lowercase",
                             textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis",
                             whiteSpace: "nowrap" }}>
                {(r.event?.title || "an event").toLowerCase()}
              </Link>
              <p style={{ fontFamily: "Arial", fontSize: 12, color: META, marginTop: 2 }}>
                by <span style={{ color: "var(--ww-text)" }}>
                  {(r.author?.display_name || r.author?.handle || "a member").toLowerCase()}
                </span>
                {r.author?.handle && <> · @{r.author.handle}</>}
              </p>
            </div>
            {typeof r.member_score === "number" && (
              <div data-testid={`feed-review-${r.id}-score`}
                   style={{ flexShrink: 0, textAlign: "right",
                            display: "flex", alignItems: "center", gap: 4 }}>
                <MemberRatingPill value={r.member_score} iko={r.event?.iko}
                                  testId={`feed-review-${r.id}-rating`} />
              </div>
            )}
          </header>
          {r.note && (
            <p data-testid={`feed-review-${r.id}-note`}
               style={{ fontFamily: "Arial", fontSize: 14, color: "var(--ww-text)",
                        lineHeight: 1.5, marginTop: 2,
                        display: "-webkit-box", WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              {r.note}
            </p>
          )}
          <Link to={`/app/event/${r.event_id}`} data-testid={`feed-review-${r.id}-open`}
                style={{ display: "inline-block", marginTop: 10, fontFamily: "Arial", fontSize: 12,
                         color: INK, textDecoration: "underline" }}>
            more
          </Link>
        </article>
      ))}
    </div>
  );
}

function EmptyLane({ icon, title, line, cta }) {
  return (
    <div className="rounded-2xl border p-8 text-center" data-testid="feed-tab-empty"
         style={{ borderColor: LINE, background: CARD }}>
      <div style={{ marginBottom: 10 }}>{icon}</div>
      <p style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 16, textTransform: "lowercase", color: "var(--ww-text)" }}>
        {title}
      </p>
      <p style={{ fontFamily: "Arial", fontSize: 13, color: META, marginTop: 4 }}>{line}</p>
      {cta && (
        <Link to={cta.href}
              style={{ display: "inline-block", marginTop: 14, padding: "10px 20px", borderRadius: 999,
                       background: YELLOW, color: "#1A1A30",
                       fontFamily: "Poppins", fontWeight: 700, textDecoration: "none", textTransform: "lowercase" }}>
          {cta.label}
        </Link>
      )}
    </div>
  );
}
